import { useEffect, useState } from 'react'
import { ExternalLink, Globe, Radio } from 'lucide-react'
import { api } from '../../../lib/api'
import { Card, EmptyState, ErrorBanner, StatusChip, Table, type Column, type StatusTone } from '../../../components/ui'
import { useDarkWebPage } from '../DarkWebContext'
import { formatDay } from '../format'

type Lookalike = Awaited<ReturnType<typeof api.getDarkWebLookalikeDomains>>['domains'][number]
type FeedItem = Awaited<ReturnType<typeof api.getDarkWebThreatFeed>>['items'][number]

const RISK_TONE: Record<string, StatusTone> = { high: 'critical', medium: 'warning', low: 'neutral' }
const RISK_LABEL: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low' }
const RISK_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 }

/**
 * External threats (#397 step 3d): lookalike domains (brand protection) and, when the
 * WhiteIntel add-on is enabled, dark web chatter mentioning the organization.
 */
export function ExternalThreatsTab() {
  const { dashboard, dataVersion } = useDarkWebPage()
  const [domains, setDomains] = useState<Lookalike[]>([])
  const [monitored, setMonitored] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [feed, setFeed] = useState<FeedItem[] | null>(null)
  const [feedUnavailable, setFeedUnavailable] = useState<string | null>(dashboard?.threatFeedAvailable === false ? '' : null)
  const [feedError, setFeedError] = useState<string | null>(null)

  async function loadLookalikes() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getDarkWebLookalikeDomains({ limit: 100 })
      if (res.success) {
        setDomains([...res.domains].sort((a, b) => (RISK_ORDER[a.risk_level] ?? 3) - (RISK_ORDER[b.risk_level] ?? 3)))
        setMonitored(res.monitoredDomains || [])
      } else {
        setError((res as { error?: string }).error || 'The server returned an error.')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error.')
    } finally {
      setLoading(false)
    }
  }

  async function loadFeed() {
    setFeedError(null)
    try {
      const res = await api.getDarkWebThreatFeed({ limit: 20 })
      if (res.unavailable === 'addon_required') setFeedUnavailable(res.message || '')
      else if (res.success) setFeed(res.items)
      else setFeedError((res as { error?: string }).error || 'The server returned an error.')
    } catch (e) {
      setFeedError(e instanceof Error ? e.message : 'Network error.')
    }
  }

  useEffect(() => {
    loadLookalikes()
    if (dashboard?.threatFeedAvailable !== false) loadFeed()
  }, [dataVersion])

  const columns: Array<Column<Lookalike>> = [
    { key: 'domain', header: 'Lookalike domain', cell: d => <span className="font-mono text-[12.5px] font-semibold">{d.domain}</span> },
    { key: 'source', header: 'Imitates', cell: d => <span className="text-ink-secondary">{d.source_domain || '—'}</span> },
    { key: 'risk', header: 'Risk', cell: d => <StatusChip tone={RISK_TONE[d.risk_level] || 'neutral'}>{RISK_LABEL[d.risk_level] || d.risk_level || 'Unknown'}</StatusChip> },
    { key: 'registered', header: 'Registered', cell: d => <span className="whitespace-nowrap text-ink-secondary">{formatDay(d.registration_date ? Date.parse(d.registration_date) : null)}</span> },
    { key: 'discovered', header: 'Discovered', cell: d => <span className="whitespace-nowrap text-ink-secondary">{formatDay(d.discovered_at ? Date.parse(d.discovered_at) : null)}</span> },
    { key: 'ip', header: 'Resolves to', cell: d => <span className="font-mono text-xs text-ink-secondary">{(d.ip_addresses || []).slice(0, 2).join(', ') || 'Not resolving'}</span> },
  ]

  return (
    <div className="flex flex-col gap-5">
      <section aria-labelledby="dw-lookalike-title" className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 id="dw-lookalike-title" className="m-0 font-heading text-base font-semibold">Lookalike domains</h2>
          <span className="text-[13px] text-ink-secondary">
            Typosquatted domains that could be used to phish your employees or customers
            {monitored.length > 0 && ` · watching ${monitored.join(', ')}`}
          </span>
        </div>
        {error && <ErrorBanner title="Couldn't load lookalike domains" description={error} onRetry={loadLookalikes} />}
        {!error && (
          <Table
            caption="Lookalike domains"
            columns={columns}
            rows={domains}
            rowKey={d => d.domain}
            loading={loading}
            minWidth={820}
            empty={<EmptyState icon={<Globe className="h-8 w-8" />} title="No lookalike domains detected" />}
            footer={domains.length > 0 ? <span>{domains.length} domains · refreshed with each scan</span> : undefined}
          />
        )}
      </section>

      <Card title="Dark web chatter" description="Mentions of your organization on forums, leak sites and Telegram">
        {feedUnavailable !== null ? (
          <EmptyState
            icon={<Radio className="h-8 w-8" />}
            title="Requires the WhiteIntel Threat Feed add-on"
            description={feedUnavailable || 'The Threat Feed add-on is not enabled on this subscription. Contact WhiteIntel to enable dark web chatter monitoring.'}
          />
        ) : feedError ? (
          <ErrorBanner title="Couldn't load the threat feed" description={feedError} onRetry={loadFeed} />
        ) : feed === null ? (
          <p className="m-0 text-sm text-muted" role="status">Loading…</p>
        ) : feed.length === 0 ? (
          <p className="m-0 text-sm text-ink-secondary">No mentions found.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {feed.map(item => (
              <li key={item.id} className="rounded-lg border border-line p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">{item.title}</span>
                  <span className="text-xs text-muted">{formatDay(item.published_at ? Date.parse(item.published_at) : null)} · {item.network}</span>
                </div>
                {item.summary && <p className="m-0 mt-1 text-[13px] text-ink-secondary">{item.summary}</p>}
                {item.link && (
                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[13px] text-link hover:underline">
                    Source <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
